/**
 * Show Up Tomorrow (PWA). Inicio de sesión con Google, libro elegido con Google Picker y leído/escrito directamente
 * desde el dispositivo (caché local + cola de cambios), y la app diaria: Fuerza guiada y Wing Chun.
 *
 * Tres capas en la página: #screen (Home o rondas), #overlay (cuenta atrás, celebración, «ronda completada») y
 * #modal (hojas). Las hojas con campos de texto no se repintan mientras se escribe.
 */
import './styles.css';
import { applyLocally, type Command, type Session, type Workbook } from '@sut/workbook';
import {
  clockString, dateKey, elapsedSeconds, exerciseId, formName, habitStats, remainingSeconds, routineFromSettings,
  serializeRotation, WING_CHUN_FORMS, decodeFeedback, encodeFeedback, type Exercise, type Measure, type MeasurementCode, type Question, type Section,
} from '@sut/domain';
import { api, ApiError, type Me } from './adapters/api-client.js';
import { sheetsSource } from './adapters/sheets-source.js';
import { sheetsApi } from './adapters/sheets-api.js';
import { pickSpreadsheet } from './adapters/picker.js';
import { indexedDbCache } from './adapters/indexeddb-cache.js';
import { claimDevice, wipeDevice } from './adapters/device.js';
import { loadCached, sync, type Loaded } from './app/sync.js';
import { queue, processQueue, type QueuedCommand } from './app/queue.js';
import type { WorkbookSource } from './app/ports.js';
import { Engine, pendingRecovery } from './app/engine.js';
import { loadPrefs, savePrefs, type Prefs } from './app/prefs.js';
import { cues } from './app/audio.js';
import { disableReminder, enableReminder, pushSupported, saveTodayState } from './app/reminder.js';
import { wake } from './app/wakelock.js';
import { newPractice, ROTATION_KEY, rotationOf, wingChunToday, type WingChunSource } from './app/wingchun.js';
import {
  celebrationView, countdownView, esc, feedbackSheet, formPickerSheet, homeView, practiceSheet, recoverySheet,
  roundFlashView, routineSheet, settingsSheet, workoutView, type FeedbackForm, type HomeUi, type PracticeForm, type RecoveryForm,
  type RoutineDraft, blockProgress, blockReviewSheet, historySheet, practiceEditSheet, sessionSheet, type PracticeEditForm,
  type SessionForm,
} from './ui/views.js';
import { blockReviewText, hasIncompleteGuidedRound, measuredExercises, promoteAfterDelete, WC_REVIEW_KEY, wingChunReport, wingChunReviewProgress } from './app/review.js';

const root = document.getElementById('app')!;
root.innerHTML = '<div id="screen"></div><div id="overlay"></div><div id="modal"></div>';
const screenEl = document.getElementById('screen')!;
const overlayEl = document.getElementById('overlay')!;
const modalEl = document.getElementById('modal')!;

const isDev = /-dev\.|^localhost$|^127\.0\.0\.1$/.test(location.hostname);
/**
 * iPhone/iPad: Safari bloquea las cookies de Google dentro del marco del selector (prevención de seguimiento entre
 * sitios), así que Google Picker no ve la sesión. Se elige el libro una vez desde el ordenador: queda guardado en el
 * servidor y el permiso de drive.file es de la cuenta, no del dispositivo.
 */
const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1);

// ---------------------------------------------------------------------------------------------------------------
// Estado

let me: Me | null = null;
let source: WorkbookSource | null = null;
let base: Loaded | null = null;
let shown: Workbook | null = null;
let pending: QueuedCommand[] = [];
/** Ya escritas en el libro pero aún no releídas: se siguen aplicando encima hasta la próxima lectura completa. */
let written: Command[] = [];
/** Pedidas en este instante y aún no en la cola (IndexedDB es asíncrono): así la interfaz nunca va por detrás. */
let inflight: Command[] = [];
let syncing = false;
let flushing = false;
let message = '';
let reauth = '';
let routineSeeded = false;
let prefs: Prefs = loadPrefs();

const home: HomeUi = { fuerzaExpanded: false, wingChunExpanded: false };

type Modal =
  | { kind: 'feedback'; form: FeedbackForm }
  | { kind: 'recovery'; form: RecoveryForm }
  | { kind: 'practice'; form: PracticeForm & { id?: string } }
  | { kind: 'picker'; source: WingChunSource }
  | { kind: 'settings' }
  | { kind: 'routine'; draft: RoutineDraft }
  | { kind: 'blockReview'; text: string; copied: boolean }
  | { kind: 'history'; tab: 'workout' | 'wingChun'; copied: boolean }
  | { kind: 'session'; form: SessionForm; back: boolean }
  | { kind: 'practiceEdit'; form: PracticeEditForm; back: boolean };
let modal: Modal | null = null;
const blankFeedback = (): FeedbackForm => ({ effort: null, activity: 'Ninguna', pain: false, painNote: '', note: '' });
/** El cuestionario se conserva aunque la hoja se cierre y se vuelva a abrir. */
let feedbackForm: FeedbackForm = blankFeedback();
/** Recuperación ya preguntada en esta visita (no se insiste). */
let recoveryAsked = '';

let reminderNote = '';
let reminderBusy = false;
let historyTab: 'workout' | 'wingChun' = 'workout';
let pendingAnswer: MeasurementCode | null = null;
let flashUntil = 0;
let countdown: { token: number; value: number | 'go' } | null = null;
let celebrated = '';

const engine = new Engine({
  workbook: () => shown,
  saveSession: (session, label) => void edit([{ type: 'upsert-session', session }], label),
  setSetting: (key, value) => void edit([{ type: 'set-setting', key, value }], `Ajuste ${key}`),
  measureLastRound: () => prefs.measureLastRound,
  countdownEnabled: () => prefs.countdownEnabled,
});

// ---------------------------------------------------------------------------------------------------------------
// Arranque

async function boot() {
  const params = new URLSearchParams(location.search);
  const loginError = params.get('login_error') ?? '';
  if (params.has('login_error')) history.replaceState(null, '', '/');
  try {
    me = await api.me();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return renderSignedOut(loginError);
    return renderMessage('No se pudo conectar con el servidor', (err as Error).message);
  }
  await claimDevice(me.email);
  const ref = me.workbooks[0];
  if (!ref) return renderPick();
  await open(ref.fileId);
}

async function open(fileId: string) {
  source = sheetsSource(fileId, () => api.accessToken());
  base = await loadCached(source, indexedDbCache);
  await apply();
  render();
  void refresh(false).then(() => flush());
  // La dirección de envío puede cambiar: se vuelve a registrar al abrir (en silencio)
  if (prefs.reminderEnabled && pushSupported() && Notification.permission === 'granted') void enableReminder(prefs.reminderTime).catch(() => {});
}

// ---------------------------------------------------------------------------------------------------------------
// Datos: libro + cambios (escritos, en cola y recién pedidos) aplicados encima = lo que se ve

async function apply() {
  if (!source || !base) { shown = null; return; }
  pending = await queue.list(source.id);
  const cmds = [...written, ...pending.filter(p => p.status === 'pending').map(p => p.cmd), ...inflight];
  shown = cmds.reduce(applyLocally, base.wb);
  engine.hydrate();
}

async function refresh(force: boolean) {
  if (!source || syncing) return;
  syncing = true; message = '';
  render();
  try {
    const fresh = await sync(source, indexedDbCache, force);
    if (fresh) { base = fresh; written = []; }
    else if (base) base = { ...base, syncedAt: new Date().toISOString(), fromCache: false };
    else base = await loadCached(source, indexedDbCache);
  } catch (err) {
    message = `No se pudo sincronizar: ${(err as Error).message}`;
  } finally {
    syncing = false;
    await apply();
    seedRoutine();
    render();
  }
}

/** Primera vez con un libro sin pestaña Rutina: se crea a partir de las claves de Settings (solo con datos frescos). */
function seedRoutine() {
  if (routineSeeded || !base || base.fromCache || !shown || shown.routine !== null) return;
  routineSeeded = true;
  void edit([{ type: 'replace-routine', rows: routineFromSettings(shown.settings) }], 'Crear pestaña Rutina');
}

/** Escribe en el libro los cambios pendientes; si alguno se guardó, vuelve a leer el libro. */
async function flush() {
  if (!source || flushing || !navigator.onLine) return;
  flushing = true;
  try {
    const r = await processQueue(source.id, sheetsApi(source.id, () => api.accessToken()));
    written.push(...r.written);
    if (r.stoppedBy) message = `Cambios pendientes: ${r.stoppedBy}`;
    // Tras escribir se relee SIEMPRE (Drive tarda unos segundos en cambiar su fecha de modificación)
    if (r.done || r.conflicts) await refresh(true);
    else { await apply(); render(); }
  } finally { flushing = false; }
  // Lo que se pidió mientras se escribía
  if (!message && pending.some(p => p.status === 'pending')) void flush();
}

let chain: Promise<void> = Promise.resolve();
/** Cambios en orden: se ven al instante y se guardan en la cola antes de intentar escribirlos. */
function edit(cmds: Command[], label: string): Promise<void> {
  if (!source || !cmds.length) return chain;
  const src = source;
  inflight.push(...cmds);
  if (shown) shown = cmds.reduce(applyLocally, shown);
  chain = chain.then(async () => {
    for (const cmd of cmds) await queue.add(src.id, cmd, label);
    inflight = inflight.filter(c => !cmds.includes(c));
    await apply();
    render();
    void flush();
  }).catch(err => { message = `No se pudo guardar el cambio: ${(err as Error).message}`; render(); });
  return chain;
}

// ---------------------------------------------------------------------------------------------------------------
// Pantallas de acceso

function renderSignedOut(error: string) {
  screenEl.innerHTML = `<div class="screen">
    <header class="hero">
      <img src="/icon-192.png" width="72" height="72" alt="">
      <p class="eyebrow">SISTEMA DIARIO</p>
      <h1>Vuelve mañana</h1>
      <p class="muted">Hoy solo tienes que aparecer.</p>
    </header>
    <section class="card">
      <p>App personal para mantener a diario dos hábitos con un mínimo pequeño: fuerza y formas de Wing Chun.
         Tus datos se guardan en <b>tu propio libro de Google Sheets</b>.</p>
      ${error ? `<p class="error">${esc(error)}</p>` : ''}
      <a class="button primary" href="/api/auth/google/start">Entrar con Google</a>
      <p class="muted small">Uso privado por invitación. <a href="/privacidad/">Privacidad</a> · <a href="/terminos/">Condiciones</a></p>
    </section></div>`;
}

function renderPick(error = '') {
  modal = null; modalEl.innerHTML = ''; lastModal = ''; lastScreen = '';
  document.body.classList.remove('modal-open');
  screenEl.innerHTML = `<div class="screen">
    <header class="home-header"><div><p class="eyebrow">${isDev ? '<span class="dev">DEV</span> ' : ''}SISTEMA DIARIO</p><h1>Vuelve mañana</h1>
      <p class="muted small">${esc(me?.email ?? '')}</p></div></header>
    <section class="card">
      <h2>Elige tu libro</h2>
      <p class="muted">Selecciona tu Google Sheet de Vuelve Mañana (pestañas <code>Sessions</code> y <code>Settings</code>).
        Con el permiso <code>drive.file</code>, la app solo podrá abrir ese fichero.</p>
      ${isDev ? '<p class="warn">Estás en DEV: elige una <b>copia</b> del libro, no el real.</p>' : ''}
      ${isIOS ? `<p class="warn">En iPhone, Safari no deja abrir el selector de Google. Entra en
        <b>${esc(location.host)}</b> desde el ordenador y elige allí el libro (solo una vez): este dispositivo lo usará solo.</p>` : ''}
      ${error ? `<p class="error">${esc(error)}</p>` : ''}
      ${isIOS ? '<button class="primary" data-act="recheck">Ya lo he elegido en el ordenador</button>' : '<button class="primary" data-act="pickWorkbook">Elegir libro</button>'}
      <button class="secondary" data-act="logout">Cerrar sesión</button>
    </section></div>`;
}

function renderMessage(title: string, detail: string) {
  screenEl.innerHTML = `<div class="screen"><section class="card"><h2>${esc(title)}</h2><p class="error">${esc(detail)}</p>
    <button class="primary" data-act="reload">Reintentar</button></section></div>`;
}

// ---------------------------------------------------------------------------------------------------------------
// Pintado

let lastScreen = '';
let lastOverlay = '';
let lastModal = '';

function render() {
  if (!me || !source) return;
  const wb = shown;
  if (!wb) {
    paint(screenEl, `<div class="screen"><section class="card"><p class="muted">${syncing ? 'Leyendo el libro…' : esc(message) || 'Sin datos todavía.'}</p>
      ${!syncing ? '<button class="primary" data-act="syncNow">Reintentar</button>' : ''}</section></div>`, 'screen');
    return;
  }
  const html = engine.phase === 'normalActive' ? workoutView(engine, prefs, pendingAnswer) : homeView(wb, engine, home, {
    syncing: syncing || flushing, pending: pending.filter(p => p.status === 'pending').length + inflight.length,
    conflicts: pending.filter(p => p.status === 'conflict').map(p => `${p.label}: ${p.error}`), message, reauth, isDev,
  });
  paint(screenEl, html, 'screen');
  renderOverlay(wb);
  renderModal(wb);
  updateBinds();
  housekeeping();
}

/** Solo se toca el DOM si el HTML cambia (un repintado a mitad de un toque se comería el toque). */
function paint(el: HTMLElement, html: string, which: 'screen' | 'overlay' | 'modal', force = false) {
  const last = which === 'screen' ? lastScreen : which === 'overlay' ? lastOverlay : lastModal;
  if (!force && html === last) return;
  if (which === 'screen') lastScreen = html; else if (which === 'overlay') lastOverlay = html; else lastModal = html;
  el.innerHTML = html;
}

function renderOverlay(wb: Workbook) {
  if (engine.phase === 'celebration') {
    // Se pinta una sola vez (confeti y sonido al entrar)
    const key = `${engine.session?.id}:${engine.session?.normalFinishedAt || 'floor'}`;
    if (celebrated !== key) {
      celebrated = key;
      cues.file(prefs.celebrationSound);
      const streak = habitStats(new Set(wb.sessions.map(s => s.date).filter(Boolean))).currentStreak;
      lastOverlay = 'celebration';
      overlayEl.innerHTML = celebrationView(!!engine.session?.normalStartedAt, streak);
    }
    return;
  }
  let html = '';
  if (engine.phase === 'countdown') {
    if (!countdown) runCountdown();
    html = countdownView(countdown?.value ?? 3);
  } else {
    countdown = null;
    if (Date.now() < flashUntil) html = roundFlashView();
  }
  paint(overlayEl, html, 'overlay');
}

function renderModal(wb: Workbook, force = false) {
  // El cuestionario es obligatorio para cerrar el día; la recuperación se pregunta al llegar a «¿Cómo estás hoy?»
  if (engine.phase === 'feedback' && !modal) modal = { kind: 'feedback', form: feedbackForm };
  if (engine.phase === 'readiness' && !modal) {
    const prev = pendingRecovery(wb);
    if (prev && recoveryAsked !== prev.id) { recoveryAsked = prev.id; modal = { kind: 'recovery', form: { session: prev, soreness: null, wantToReturn: null } }; }
  }
  // Mientras se escribe en un campo de la hoja, no se repinta (se perdería el teclado)
  const typing = modalEl.contains(document.activeElement) && /INPUT|TEXTAREA/.test(document.activeElement?.tagName ?? '');
  if (typing && !force) return;
  let html = '';
  if (modal?.kind === 'feedback') html = feedbackSheet(modal.form);
  else if (modal?.kind === 'recovery') html = recoverySheet(modal.form);
  else if (modal?.kind === 'practice') html = practiceSheet(modal.form);
  else if (modal?.kind === 'picker') {
    const wc = wingChunToday(wb);
    const extra = modal.source === 'extraPractice';
    html = formPickerSheet(extra ? WING_CHUN_FORMS.map(f => f.id) : wc.eligible, wc.suggested, extra, wc.average);
  } else if (modal?.kind === 'settings') {
    const c = engine.config;
    html = settingsSheet(prefs, {
      email: me?.email ?? '', workbookName: base?.title || me?.workbooks[0]?.name || '—', syncedAt: base?.syncedAt ?? '',
      fromCache: !!base?.fromCache, pending: pending.length + inflight.length, warnings: wb.warnings, rotation: rotationOf(wb),
      floor: c.floor, normal: c.normal, maxRounds: c.maxRounds, ceilingMinutes: c.ceilingSeconds / 60, isIOS,
      pushSupported: pushSupported(), reminderNote, reminderBusy,
    });
  } else if (modal?.kind === 'routine') html = routineSheet(modal.draft);
  else if (modal?.kind === 'blockReview') html = blockReviewSheet(modal.text, modal.copied);
  else if (modal?.kind === 'history') html = historySheet(wb, modal.tab, modal.copied);
  else if (modal?.kind === 'session') html = sessionSheet(modal.form, engine.config);
  else if (modal?.kind === 'practiceEdit') html = practiceEditSheet(modal.form);
  document.body.classList.toggle('modal-open', !!html);
  // Repintar la misma hoja: sin animación de entrada y en la misma posición de scroll
  const kind = modal?.kind ?? '';
  const same = !!kind && modalEl.dataset.kind === kind;
  const top = modalEl.querySelector('.sheet-body')?.scrollTop ?? 0;
  paint(modalEl, html, 'modal', force);
  modalEl.dataset.kind = kind;
  modalEl.classList.toggle('still', same);
  const body = modalEl.querySelector('.sheet-body');
  if (same && body && body.scrollTop !== top) body.scrollTop = top;
}

/** Relojes y barra: se actualizan sin repintar. */
function updateBinds() {
  const now = Date.now();
  const c = engine.clock;
  const set = (name: string, text: string) => document.querySelectorAll<HTMLElement>(`[data-bind="${name}"]`).forEach(el => { if (el.textContent !== text) el.textContent = text; });
  if (c) {
    set('elapsed', clockString(elapsedSeconds(c, now)));
    set('remaining', clockString(Math.ceil(remainingSeconds(c, now))));
    const bar = document.querySelector<HTMLElement>('[data-bind="progress"]');
    if (bar) bar.style.width = `${Math.min(100, (elapsedSeconds(c, now) / c.ceilingSeconds) * 100)}%`;
  }
  set('warmup', clockString(engine.warmupElapsed(now)));
  if (modal?.kind === 'practice') set('practice', clockString(modal.form.startedAt ? (now - modal.form.startedAt) / 1000 : 0));
}

// Pantalla encendida, temporizador y globo del icono
let holdingAwake = false;
let timer: number | undefined;
let lastBadge = -1;
let lastState = '';

function housekeeping() {
  const practiceTiming = modal?.kind === 'practice' && !!modal.form.startedAt;
  const awake = engine.wantsAwake || practiceTiming;
  if (awake && !holdingAwake) { wake.hold(); holdingAwake = true; }
  if (!awake && holdingAwake) { wake.release(); holdingAwake = false; }

  const ticking = engine.ticking || practiceTiming || Date.now() < flashUntil;
  if (ticking && timer === undefined) timer = window.setInterval(onTick, 250);
  if (!ticking && timer !== undefined) { clearInterval(timer); timer = undefined; }

  const wcDone = wingChunToday(shown).isDone;
  const badge = (engine.trainedToday ? 0 : 1) + (wcDone ? 0 : 1);
  const state = { date: dateKey(), fuerza: engine.trainedToday, wingChun: wcDone };
  if (JSON.stringify(state) !== lastState) { lastState = JSON.stringify(state); void saveTodayState(state); }
  if (badge !== lastBadge && 'setAppBadge' in navigator) {
    lastBadge = badge;
    (badge ? navigator.setAppBadge(badge) : navigator.clearAppBadge()).catch(() => { /* sin permiso de notificaciones */ });
  }
}

let lastDay = dateKey();
function onTick() {
  if (engine.tick()) { cues.maxTimeReached(); render(); return; }
  if (flashUntil && Date.now() >= flashUntil) { flashUntil = 0; render(); }
  if (dateKey() !== lastDay) { lastDay = dateKey(); void apply().then(render); }
  updateBinds();
}

function runCountdown() {
  const token = Date.now();
  countdown = { token, value: 3 };
  cues.countdownTick();
  const step = (value: number | 'go', delay: number, after?: () => void) => window.setTimeout(() => {
    if (countdown?.token !== token) return;
    countdown.value = value;
    if (value === 'go') cues.countdownGo(); else cues.countdownTick();
    paint(overlayEl, countdownView(value), 'overlay');
    after?.();
  }, delay);
  step(2, 1000); step(1, 2000); step('go', 3000, () => window.setTimeout(() => finishCountdown(token), 500));
}

function finishCountdown(token?: number) {
  if (!countdown || (token !== undefined && countdown.token !== token)) return;
  countdown = null;
  engine.beginRounds();
  cues.nextRound();
  render();
}

// ---------------------------------------------------------------------------------------------------------------
// Acciones (toques en elementos con data-act)

function closeModal() {
  const was = modal;
  modal = (was?.kind === 'session' || was?.kind === 'practiceEdit') && was.back ? { kind: 'history', tab: historyTab, copied: false } : null;
  if (was?.kind === 'practice' && was.form.source === 'strengthFlow') engine.proceedFromOffer();
  render();
}

function openPractice(form: string, src: WingChunSource) {
  const wc = wingChunToday(shown);
  modal = { kind: 'practice', form: {
    form, source: src, countsAsMinimum: wc.eligible.includes(form) && !wc.isDone, average: wc.average(form),
    startedAt: null, step: 'timing', timed: false, result: null, outcome: '', note: '',
  } };
  render();
}

function recordPractice(p: PracticeForm & { id?: string }, durationSeconds: number | null) {
  const { practice, result } = newPractice(shown, p.form, p.source, p.startedAt ? new Date(p.startedAt) : null, durationSeconds);
  Object.assign(p, { result, timed: durationSeconds !== null, startedAt: null, step: 'done', id: practice.id });
  if (result.kind === 'minimum') cues.wingChunDaily(); else cues.wingChunExtra();
  void edit([{ type: 'upsert-wingchun', practice }, ...seedWingChunReview()], `Wing Chun: ${formName(p.form)}`);
}

/** El ciclo de revisión de Wing Chun empieza con la primera práctica registrada desde la web. */
function seedWingChunReview(): Command[] {
  return shown && !shown.settings[WC_REVIEW_KEY] ? [{ type: 'set-setting', key: WC_REVIEW_KEY, value: dateKey() }] : [];
}

async function copy(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}

const actions: Record<string, (arg: string) => void> = {
  reload: () => location.reload(),
  logout: () => void logout(),
  recheck: () => void (async () => {
    try {
      me = await api.me();
      const ref = me.workbooks[0];
      if (ref) await open(ref.fileId);
      else renderPick('Todavía no hay ningún libro elegido. Elígelo desde el ordenador y vuelve a pulsar.');
    } catch (err) { renderPick((err as Error).message); }
  })(),
  pickWorkbook: () => void (async () => {
    try {
      const picked = await pickSpreadsheet(await api.config(), await api.accessToken(), 'Elige tu libro de Vuelve Mañana');
      if (!picked) return;
      const saved = await api.setWorkbook(picked.id);
      me = me && { ...me, workbooks: [{ kind: 'main', fileId: saved.fileId, name: saved.name }] };
      await open(saved.fileId);
    } catch (err) { renderPick((err as Error).message); }
  })(),
  changeWorkbook: () => renderPick(),
  syncNow: () => void refresh(true).then(() => flush()),

  openSettings: () => { modal = { kind: 'settings' }; render(); },
  closeModal,
  toggleFuerza: () => { home.fuerzaExpanded = !home.fuerzaExpanded; render(); },
  toggleWingChun: () => { home.wingChunExpanded = !home.wingChunExpanded; render(); },

  // Fuerza
  beginDay: () => { engine.beginDay(); render(); },
  startWarmup: () => { engine.startWarmup(); render(); },
  finishWarmup: () => { engine.finishWarmup(); render(); },
  skipWarmup: () => { engine.skipWarmup(); render(); },
  completeFloor: () => { engine.completeFloor(); render(); },
  proceedFromOffer: () => { engine.proceedFromOffer(); render(); },
  startNormal: () => {
    feedbackForm = blankFeedback();
    engine.startNormal();
    if (engine.phase === 'normalActive') cues.nextRound();
    render();
  },
  finishFloorOnly: () => { feedbackForm = blankFeedback(); engine.finishFloorOnly(); render(); },
  skipCountdown: () => finishCountdown(),
  completeRound: () => {
    const r = engine.completeRound();
    if (r === 'next') {
      cues.roundComplete();
      flashUntil = Date.now() + 1100;
      window.setTimeout(() => { if (engine.phase === 'normalActive') cues.nextRound(); }, 1000);
    } else if (r === 'ceiling') cues.maxTimeReached();
    render();
  },
  answer: code => {
    if (pendingAnswer) return;
    pendingAnswer = code;
    render();
    // La respuesta se ve marcada un instante antes de pasar al siguiente ejercicio
    window.setTimeout(() => {
      pendingAnswer = null;
      if (engine.recordAnswer(code) === 'ceiling') cues.maxTimeReached();
      render();
    }, 250);
  },
  revise: () => { engine.revisePrevious(); render(); },
  pause: () => { engine.pause(); render(); },
  resume: () => { engine.resume(); render(); },
  finishEarly: () => { engine.finishEarly(); render(); },
  proceedToFeedback: () => { engine.proceedToFeedback(); render(); },
  openFeedback: () => {
    if (engine.phase === 'celebration') engine.proceedToFeedback();
    modal = { kind: 'feedback', form: feedbackForm };
    render();
  },

  // Cuestionario
  fbEffort: v => { feedbackForm.effort = Number(v); renderModal(shown!, true); },
  fbActivity: v => { feedbackForm.activity = v; renderModal(shown!, true); },
  fbPain: v => { feedbackForm.pain = v === 'true'; renderModal(shown!, true); },
  saveFeedback: () => {
    const f = feedbackForm;
    if (f.effort === null) return;
    engine.submitFeedback({ effort: f.effort, activity: f.activity, pain: !!f.pain, painNote: f.painNote, note: f.note });
    modal = null;
    render();
  },

  // Recuperación del día anterior
  recSoreness: v => { if (modal?.kind === 'recovery') { modal.form.soreness = Number(v); renderModal(shown!, true); } },
  recWant: v => { if (modal?.kind === 'recovery') { modal.form.wantToReturn = v === 'true'; renderModal(shown!, true); } },
  saveRecovery: () => {
    if (modal?.kind !== 'recovery') return;
    const { session, soreness, wantToReturn } = modal.form;
    if (soreness === null || wantToReturn === null) return;
    const updated: Session = { ...session, nextDaySoreness: soreness, wantToReturn, recoveryLoggedAt: new Date().toISOString() };
    modal = null;
    void edit([{ type: 'upsert-session', session: updated }], 'Recuperación');
    render();
  },

  // Wing Chun
  practice: arg => { const [form, src] = arg.split('|') as [string, WingChunSource]; openPractice(form, src); },
  quickMark: form => {
    const { practice, result } = newPractice(shown, form, 'wingChunCard', null, null);
    if (result.kind === 'minimum') cues.wingChunDaily(); else cues.wingChunExtra();
    void edit([{ type: 'upsert-wingchun', practice }, ...seedWingChunReview()], `Wing Chun: ${formName(form)}`);
    render();
  },
  pickForm: src => { modal = { kind: 'picker', source: src as WingChunSource }; render(); },
  pickedForm: form => { if (modal?.kind === 'picker') openPractice(form, modal.source); },
  practiceStart: () => { if (modal?.kind === 'practice') { modal.form.startedAt = Date.now(); render(); } },
  practiceFinish: () => {
    if (modal?.kind !== 'practice' || !modal.form.startedAt) return;
    recordPractice(modal.form, (Date.now() - modal.form.startedAt) / 1000);
    render();
  },
  practiceQuick: () => { if (modal?.kind === 'practice') { recordPractice(modal.form, null); render(); } },
  practiceOutcome: v => { if (modal?.kind === 'practice') { modal.form.outcome = modal.form.outcome === v ? '' : v; renderModal(shown!, true); } },
  practiceSave: () => {
    if (modal?.kind !== 'practice') return;
    const p = modal.form;
    const saved = shown?.wingChun.find(x => x.id === p.id);
    if (saved && (p.outcome || p.note.trim())) void edit([{ type: 'upsert-wingchun', practice: { ...saved, outcome: p.outcome, note: p.note.trim() } }], 'Wing Chun: valoración');
    closeModal();
  },
  practiceClose: () => closeModal(),

  // Revisión del bloque con ChatGPT
  openBlockReview: () => {
    if (!shown) return;
    modal = { kind: 'blockReview', text: blockReviewText(shown, blockProgress(shown), engine.config), copied: false };
    render();
  },
  copyBlockReview: () => void (async () => {
    if (modal?.kind !== 'blockReview') return;
    modal.copied = await copy(modal.text);
    if (!modal.copied) message = 'No se pudo copiar: mantén pulsado el texto para seleccionarlo';
    render();
  })(),
  markBlockReviewed: () => void (async () => {
    if (modal?.kind !== 'blockReview' || !shown) return;
    await copy(modal.text);
    const p = blockProgress(shown);
    modal = null;
    void edit([
      { type: 'set-setting', key: 'CALIBRATION_BASE_NORMAL_COUNT', value: String(p.base + p.target) },
      { type: 'set-setting', key: 'CALIBRATION_BLOCK_NUMBER', value: String(p.blockNumber + 1) },
    ], 'Bloque revisado');
    render();
  })(),
  copyWingChunReview: () => void (async () => {
    if (!shown) return;
    const due = wingChunReviewProgress(shown).isDue;
    const ok = await copy(wingChunReport(shown, rotationOf(shown)));
    if (modal?.kind === 'history') modal.copied = ok;
    if (!ok) message = 'No se pudo copiar la revisión';
    // Copiar una revisión ya debida cierra el ciclo y empieza otro hoy
    if (ok && due) void edit([{ type: 'set-setting', key: WC_REVIEW_KEY, value: dateKey() }], 'Revisión de Wing Chun');
    render();
  })(),

  // Historial
  openHistory: () => { modal = { kind: 'history', tab: historyTab, copied: false }; render(); },
  historyTab: tab => { historyTab = tab as typeof historyTab; modal = { kind: 'history', tab: historyTab, copied: false }; renderModal(shown!, true); },
  editSession: id => {
    const s = shown?.sessions.find(x => x.id === id);
    if (!s) return;
    modal = { kind: 'session', back: modal?.kind === 'history', form: {
      original: s, effort: s.effort, activity: s.activity || 'Ninguna', pain: s.pain ?? false, painNote: s.painNote, note: s.note,
      soreness: s.nextDaySoreness, wantToReturn: s.wantToReturn, answers: decodeFeedback(s.exerciseFeedback), confirmDelete: false,
    } };
    render();
  },
  sdAnswer: arg => { if (modal?.kind !== 'session') return; const [id, code] = arg.split('|') as [string, string]; modal.form.answers[id] = code; renderModal(shown!, true); },
  sdActivity: v => { if (modal?.kind === 'session') { modal.form.activity = v; renderModal(shown!, true); } },
  sdEffort: v => { if (modal?.kind === 'session') { modal.form.effort = Number(v); renderModal(shown!, true); } },
  sdPain: v => { if (modal?.kind === 'session') { modal.form.pain = v === 'true'; renderModal(shown!, true); } },
  sdSoreness: v => { if (modal?.kind === 'session') { modal.form.soreness = Number(v); renderModal(shown!, true); } },
  sdWant: v => { if (modal?.kind === 'session') { modal.form.wantToReturn = v === 'true'; renderModal(shown!, true); } },
  sdSave: () => {
    if (modal?.kind !== 'session') return;
    const f = modal.form;
    const s = f.original;
    const c = engine.config;
    let next: Session = {
      ...s, effort: f.effort, activity: f.activity, pain: f.pain, painNote: f.pain ? f.painNote.trim() : '', note: f.note.trim(),
      nextDaySoreness: f.soreness, wantToReturn: f.wantToReturn,
      recoveryLoggedAt: s.recoveryLoggedAt || (f.soreness !== null || f.wantToReturn !== null ? new Date().toISOString() : ''),
    };
    if (Object.keys(decodeFeedback(s.exerciseFeedback)).length) {
      next = { ...next, exerciseFeedback: encodeFeedback(f.answers, measuredExercises(s, c).map(e => e.id)) };
      // Que las rondas sigan cuadrando si la corrección completa (o rompe) la última ronda
      const was = hasIncompleteGuidedRound(s, c), now = hasIncompleteGuidedRound(next, c);
      if (was && !now) next.rounds += 1; else if (!was && now) next.rounds = Math.max(0, next.rounds - 1);
    }
    void edit([{ type: 'upsert-session', session: next }], `Editar día ${s.date}`);
    closeModal();
  },
  sdDelete: () => {
    if (modal?.kind !== 'session') return;
    if (!modal.form.confirmDelete) { modal.form.confirmDelete = true; renderModal(shown!, true); return; }
    void edit([{ type: 'delete-session', id: modal.form.original.id }], `Eliminar día ${modal.form.original.date}`);
    closeModal();
  },
  editPractice: id => {
    const x = shown?.wingChun.find(p => p.id === id);
    if (!x) return;
    modal = { kind: 'practiceEdit', back: modal?.kind === 'history', form: { original: x, outcome: x.outcome, note: x.note, confirmDelete: false } };
    render();
  },
  peOutcome: v => { if (modal?.kind === 'practiceEdit') { modal.form.outcome = modal.form.outcome === v ? '' : v; renderModal(shown!, true); } },
  peSave: () => {
    if (modal?.kind !== 'practiceEdit') return;
    const f = modal.form;
    void edit([{ type: 'upsert-wingchun', practice: { ...f.original, outcome: f.outcome, note: f.note.trim() } }], 'Editar práctica');
    closeModal();
  },
  peDelete: () => {
    if (modal?.kind !== 'practiceEdit' || !shown) return;
    if (!modal.form.confirmDelete) { modal.form.confirmDelete = true; renderModal(shown, true); return; }
    const x = modal.form.original;
    const promoted = promoteAfterDelete(shown, x, wingChunToday(shown).eligible);
    void edit([{ type: 'delete-wingchun', id: x.id }, ...(promoted ? [{ type: 'upsert-wingchun', practice: promoted } as Command] : [])], 'Eliminar práctica');
    closeModal();
  },

  // Editor de rutina
  openRoutine: () => {
    const wb = shown!;
    const c = engine.config;
    modal = { kind: 'routine', draft: {
      rows: structuredClone(wb.routine ?? routineFromSettings(wb.settings)), maxRounds: c.maxRounds, ceilingMinutes: c.ceilingSeconds / 60, dirty: false,
    } };
    render();
  },
  rtRounds: arg => routineEdit(d => { d.maxRounds = clamp(d.maxRounds + Number(arg.split('|')[1]), 1, 10); }),
  rtCeiling: arg => routineEdit(d => { d.ceilingMinutes = clamp(d.ceilingMinutes + Number(arg.split('|')[1]), 1, 60); }),
  rtTarget: arg => routineEdit(d => { const [i, by] = arg.split('|').map(Number) as [number, number]; const e = d.rows[i]!; e.target = clamp(e.target + by * (e.measure === 'seconds' ? 5 : 1), 1, 999); }),
  rtMeasure: arg => routineEdit(d => { const [i, m] = arg.split('|') as [string, Measure]; const e = d.rows[Number(i)]!; e.measure = m; if (m === 'seconds') e.question = 'done'; else if (e.question === 'done') e.question = 'rir'; }),
  rtQuestion: arg => routineEdit(d => { const [i, q] = arg.split('|') as [string, Question]; const e = d.rows[Number(i)]!; e.question = q; if (q === 'control' && !e.prompt) e.prompt = '¿Has mantenido el control lumbar?'; }),
  rtMove: arg => routineEdit(d => {
    const [i, by] = arg.split('|').map(Number) as [number, number];
    const e = d.rows[i]!;
    const peers = d.rows.filter(x => x.section === e.section && x.active).sort((a, b) => a.order - b.order);
    const k = peers.indexOf(e);
    const other = peers[k + by];
    if (other) [e.order, other.order] = [other.order, e.order];
    d.rows.sort((a, b) => (a.section === b.section ? a.order - b.order : a.section === 'FLOOR' ? -1 : 1));
  }),
  rtArchive: i => routineEdit(d => { d.rows[Number(i)]!.active = false; }),
  rtRestore: i => routineEdit(d => { const e = d.rows[Number(i)]!; e.active = true; e.order = Math.max(0, ...d.rows.filter(x => x.section === e.section).map(x => x.order)) + 1; }),
  rtAdd: sec => routineEdit(d => {
    const section = sec as Section;
    const order = Math.max(0, ...d.rows.filter(x => x.section === section).map(x => x.order)) + 1;
    const row: Exercise = { id: '', section, order, name: '', measure: 'reps', target: 5, question: 'rir', prompt: '', active: true };
    d.rows.push(row);
    d.rows.sort((a, b) => (a.section === b.section ? a.order - b.order : a.section === 'FLOOR' ? -1 : 1));
  }, true),
  rtSave: () => {
    if (modal?.kind !== 'routine') return;
    const d = modal.draft;
    const rows = d.rows.filter(e => e.name.trim() || e.id);
    // Orden 1..n por sección; los nuevos reciben un id a partir del nombre (sin chocar con los de su sección)
    for (const sec of ['FLOOR', 'NORMAL'] as Section[]) {
      const list = rows.filter(e => e.section === sec).sort((a, b) => Number(b.active) - Number(a.active) || a.order - b.order);
      const taken = new Set(list.map(e => e.id).filter(Boolean));
      list.forEach((e, k) => {
        e.name = e.name.trim() || 'Ejercicio';
        e.order = k + 1;
        if (!e.id) { e.id = exerciseId(e.name, taken); taken.add(e.id); }
      });
    }
    rows.sort((a, b) => (a.section === b.section ? a.order - b.order : a.section === 'FLOOR' ? -1 : 1));
    const st = shown?.settings ?? {};
    const cmds: Command[] = [{ type: 'replace-routine', rows }];
    if (String(d.maxRounds) !== st.MAX_ROUNDS) cmds.push({ type: 'set-setting', key: 'MAX_ROUNDS', value: String(d.maxRounds) });
    if (String(d.ceilingMinutes) !== st.CEILING_MINUTES) cmds.push({ type: 'set-setting', key: 'CEILING_MINUTES', value: String(d.ceilingMinutes) });
    modal = { kind: 'settings' };
    void edit(cmds, 'Rutina');
    render();
  },

  // Recordatorio
  toggleReminder: () => void (async () => {
    reminderBusy = true; reminderNote = ''; render();
    try {
      if (prefs.reminderEnabled) { await disableReminder(); prefs = savePrefs({ reminderEnabled: false }); reminderNote = 'Recordatorio desactivado.'; }
      else { await enableReminder(prefs.reminderTime); prefs = savePrefs({ reminderEnabled: true }); reminderNote = `Listo: te avisaré cada día a las ${prefs.reminderTime}.`; }
    } catch (err) { reminderNote = (err as Error).message; }
    reminderBusy = false; render();
  })(),
  testReminder: () => void (async () => {
    reminderBusy = true; reminderNote = ''; render();
    try {
      const r = await api.testPush();
      reminderNote = r.sent ? 'Aviso enviado: debería llegarte en unos segundos (aunque tengas la app abierta, mira el centro de notificaciones).' : 'No hay ningún dispositivo con el recordatorio activo.';
    } catch (err) { reminderNote = (err as Error).message; }
    reminderBusy = false; render();
  })(),

  // Ajustes
  togglePref: () => { prefs = savePrefs({ countdownEnabled: !prefs.countdownEnabled }); render(); },
  toggleMeasure: () => { prefs = savePrefs({ measureLastRound: !prefs.measureLastRound }); render(); },
  textScale: v => { prefs = savePrefs({ textScale: Number(v) }); render(); },
  celebrationSound: id => { prefs = savePrefs({ celebrationSound: id }); cues.file(id); render(); },
  toggleForm: id => {
    const current = rotationOf(shown);
    const next = current.includes(id) ? current.filter(f => f !== id) : [...current, id];
    void edit([{ type: 'set-setting', key: ROTATION_KEY, value: serializeRotation(next.length ? next : ['siu_nim_tau']) }], 'Rotación de Wing Chun');
    render();
  },
};

document.addEventListener('click', e => {
  const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act]');
  if (!el || (el as HTMLButtonElement).disabled) return;
  const fn = actions[el.dataset.act!];
  if (fn) { e.preventDefault(); fn(el.dataset.arg ?? ''); }
});

// Texto de las hojas: se guarda al escribir (sin repintar)
document.addEventListener('input', e => {
  const el = e.target as HTMLInputElement;
  const field = el.dataset.field;
  if (!field) return;
  if (modal?.kind === 'feedback' && (field === 'painNote' || field === 'note')) feedbackForm[field] = el.value;
  if (modal?.kind === 'practice' && field === 'note') modal.form.note = el.value;
  if (modal?.kind === 'session' && field === 'sdPainNote') modal.form.painNote = el.value;
  if (modal?.kind === 'session' && field === 'sdNote') modal.form.note = el.value;
  if (modal?.kind === 'practiceEdit' && field === 'peNote') modal.form.note = el.value;
  if (modal?.kind === 'routine') {
    const [name, i] = field.split(':');
    const row = modal.draft.rows[Number(i)];
    if (!row) return;
    if (name === 'name') row.name = el.value;
    if (name === 'target') { const v = Math.round(Number(el.value)); if (v >= 1) row.target = Math.min(999, v); }
    // Sin repintar (se perdería el teclado): solo se activa «Guardar»
    modal.draft.dirty = true;
    modalEl.querySelector('[data-act="rtSave"]')?.removeAttribute('disabled');
  }
});

// Hora del recordatorio: se guarda al cambiarla
document.addEventListener('change', e => {
  const el = e.target as HTMLInputElement;
  if (el.dataset.field !== 'reminderTime' || !/^\d{2}:\d{2}$/.test(el.value)) return;
  prefs = savePrefs({ reminderTime: el.value });
  if (prefs.reminderEnabled) void enableReminder(el.value).then(() => { reminderNote = `Hora cambiada: ${el.value}.`; }, err => { reminderNote = (err as Error).message; }).then(() => renderModal(shown!, true));
});

// iOS solo deja sonar el audio tras un toque: el primero deja preparados los sonidos
document.addEventListener('pointerdown', () => cues.unlock(prefs.celebrationSound), { passive: true });

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/** Cambio en el borrador de la rutina; con `focusNew`, el foco va al nombre del ejercicio recién añadido. */
function routineEdit(fn: (d: RoutineDraft) => void, focusNew = false) {
  if (modal?.kind !== 'routine') return;
  fn(modal.draft);
  modal.draft.dirty = true;
  renderModal(shown!, true);
  if (focusNew) {
    const empty = [...modalEl.querySelectorAll<HTMLInputElement>('input.name')].find(x => !x.value);
    empty?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    empty?.focus();
  }
}

async function logout() {
  await disableReminder().catch(() => {});
  try { await api.logout(); } catch { /* la sesión ya no existía */ }
  wake.reset();
  await wipeDevice();
  location.href = '/';
}

addEventListener('sut-reauth', e => { reauth = (e as CustomEvent<string>).detail; render(); });
addEventListener('online', () => { void flush(); });
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible' || !source) return;
  // Al volver: el techo pudo pasar con la app en segundo plano, y el día pudo cambiar
  if (engine.tick()) cues.maxTimeReached();
  lastDay = dateKey();
  void apply().then(() => { render(); return refresh(false); }).then(() => flush());
});

if ('serviceWorker' in navigator && import.meta.env.PROD) navigator.serviceWorker.register('/sw.js').catch(() => { /* sin modo sin conexión */ });

void boot();
